import { HttpStatusCode } from '@angular/common/http';
import { catchError, concatMap, defer, from, map, Observable, of, toArray } from 'rxjs';
import { getErrorStatus, resolveApiErrorMessage } from './api-error';

/** `processing` marks a 503 from the document service: the file may still land in EzShare. */
export type DocumentUploadStatus = 'pending' | 'uploading' | 'done' | 'failed' | 'processing';

export interface DocumentUploadEntry {
  file: File;
  status: DocumentUploadStatus;
  message: string | null;
}

export type DocumentUploadOutcome<T> =
  | { item: T; file: File; ok: true; res: unknown }
  | { item: T; file: File; ok: false; err: unknown; message: string; temporary: boolean };

export interface DocumentUploadSummary<T> {
  succeeded: DocumentUploadOutcome<T>[];
  failed: Extract<DocumentUploadOutcome<T>, { ok: false }>[];
  /** Failed files that are safe to upload again (503 ones are excluded because they may already exist). */
  retryableFiles: File[];
}

export function createDocumentUploadEntries(files: readonly File[]): DocumentUploadEntry[] {
  return files.map((file) => ({ file, status: 'pending', message: null }));
}

/** Returns a copy of `entries` with the entry for `file` updated. */
export function updateDocumentUploadEntry(
  entries: readonly DocumentUploadEntry[],
  file: File,
  status: DocumentUploadStatus,
  message: string | null = null,
): DocumentUploadEntry[] {
  return entries.map((entry) => (entry.file === file ? { ...entry, status, message } : entry));
}

export function isDocumentServiceUnavailable(error: unknown): boolean {
  return getErrorStatus(error) === HttpStatusCode.ServiceUnavailable;
}

export function describeDocumentUploadError(fileName: string, error: unknown): string {
  if (isDocumentServiceUnavailable(error)) {
    return `EzShare is still processing '${fileName}'. Please refresh in a minute; if it doesn't appear, upload it again.`;
  }

  return resolveApiErrorMessage(`Unable to upload '${fileName}'.`, error);
}

/**
 * Uploads files one at a time and never aborts the batch: each failure is captured in its outcome.
 * Nothing is retried automatically, since a failed request may still have stored the file.
 */
export function uploadDocumentsSequentially<T extends { file: File }>(
  items: readonly T[],
  upload: (item: T) => Observable<unknown>,
  onStatus?: (file: File, status: DocumentUploadStatus, message: string | null) => void,
): Observable<DocumentUploadOutcome<T>[]> {
  return from(items).pipe(
    concatMap((item) =>
      defer(() => {
        onStatus?.(item.file, 'uploading', null);
        return upload(item);
      }).pipe(
        map((res): DocumentUploadOutcome<T> => {
          onStatus?.(item.file, 'done', null);
          return { item, file: item.file, ok: true, res };
        }),
        catchError((err: unknown) => {
          const temporary = isDocumentServiceUnavailable(err);
          const message = describeDocumentUploadError(item.file.name, err);
          onStatus?.(item.file, temporary ? 'processing' : 'failed', message);
          return of<DocumentUploadOutcome<T>>({ item, file: item.file, ok: false, err, message, temporary });
        }),
      ),
    ),
    toArray(),
  );
}

export function summarizeDocumentUploads<T>(outcomes: readonly DocumentUploadOutcome<T>[]): DocumentUploadSummary<T> {
  const succeeded = outcomes.filter((outcome) => outcome.ok);
  const failed = outcomes.filter((outcome): outcome is Extract<DocumentUploadOutcome<T>, { ok: false }> => !outcome.ok);

  return {
    succeeded,
    failed,
    retryableFiles: failed.filter((outcome) => !outcome.temporary).map((outcome) => outcome.file),
  };
}

export function buildDocumentUploadToast<T>(summary: DocumentUploadSummary<T>): {
  message: string;
  variant: 'success' | 'destructive' | 'warning';
} {
  const total = summary.succeeded.length + summary.failed.length;
  if (summary.failed.length === 0) {
    return { message: `${total} document(s) uploaded successfully.`, variant: 'success' };
  }

  if (summary.succeeded.length === 0) {
    return { message: `None of the ${total} document(s) could be uploaded. See the details in the upload form.`, variant: 'destructive' };
  }

  return {
    message: `${summary.succeeded.length} of ${total} document(s) uploaded. ${summary.failed.length} failed; see the details in the upload form.`,
    variant: 'warning',
  };
}
